/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import * as Yup from "yup";
import { FormikProps, useFormik } from "formik";
import { FormInput, Forms, FormSelect, GradientButton, notify, TFormSelectOption } from "@housebets/housebets-x-package";

import apiWallet from "app/api/wallet";
import {useAppSelector} from "hooks/useAppSelector";

import { CurrencyConfig } from "app/components/features/wallet";
import { setLoginType } from "app/store/slices/setting";
import { CurrencySymbol, LoginType } from "app/types/common";
import { useGTMEvents } from "app/hooks/useGTMEvents";

import { IWithdrawCashFormProps, TFormikConfig, TPayload } from "./types";
import pkg from 'lodash';
import { useDispatch } from "react-redux";
const {sortBy} = pkg;

const localeKeys: { [key: string]: string } = {
    '11': "WithdrawError_11",
    '-14': "WithdrawError2FA",
    '-15': "WithdrawErrorNoDeposit",
    '-17': "WithdrawErrorInProgress",
    '-22': "WithdrawErrorSingleLimit"
};

export const WithdrawCashForm: React.FC<IWithdrawCashFormProps> = ({
    category,
}) => {
    const dispatch = useDispatch();
    const { $t } = useIntl();
    const { form } = useGTMEvents();
    const payProviderList = useAppSelector((state) => state.config.platformConfig?.payProviderList || []);

    const depositCashConfig = useMemo(() => {
        return CurrencyConfig.find((c) => c.type === category?.id)!.withdraw;
    }, [category]);

    const channelList: TFormSelectOption[] = useMemo(() => {
        const channels = payProviderList.filter((p) => (p.tokenId === category?.id && p.canDeposit === 1)) || [];
        const sortedArr = sortBy(channels, (item) => item.listOrder);
        return sortedArr.map((v) => {
            return {
                id: v.providerName || "",
                name: v.providerName || "",
            };
        });
    }, [category, payProviderList]);

    const generateInitialValues = (channelCode: string): TFormikConfig => {
        const fields = depositCashConfig[channelCode] || [];
        const initialValues: TFormikConfig = {
            channelCode: channelCode,
            amount: "",
        };

        fields.forEach((field) => {
            initialValues[field.name] = "";
        });

        return initialValues;
    };

    const formErrorEvent = (key: string) => {
        form.event({
            event: 'form_error',
            form_id: 'withdraw_cash_form',
            form_name: 'Withdraw Cash Form',
            additional_parameters: {
                message: $t({ id: localeKeys[key] }),
            }
        });
    }

    const initData: TFormikConfig = {
        channelCode: channelList[0].id,
        amount: "",
    };

    const formSchema = (channelCode: string) => {
        const fields = depositCashConfig[channelCode] || [];
        const shape: { [key: string]: any } = {
            channelCode: Yup.string().required($t({ id: "FieldIsRequired" })),
            amount: Yup.string().required($t({ id: "FieldIsRequired" })),
        };

        fields.forEach((field) => {
            if (field.type === "select" || field.type === "input" || field.type === "phone") {
                shape[field.name] = Yup.string().required($t({ id: "FieldIsRequired" }));
            }
        });

        return Yup.object().shape(shape);
    };

    const formik: FormikProps<TFormikConfig> = useFormik<TFormikConfig>({
        initialValues: generateInitialValues(initData.channelCode + ""),
        validationSchema: formSchema(initData.channelCode + ""),
        validateOnBlur: true,
        onSubmit: async (_data) => {
            const payload: TPayload = {
                ..._data,
                amount: Number(_data.amount),
                currency: category?.title || "",
            };
            const { status, data } = await apiWallet.withdrawFiat(payload);
            if (data.code === 11) {
                notify({
                    type: "error",
                    title: $t({ id: "WithdrawError_11" }),
                });
                formErrorEvent('11');
                return;
            }
            if (data.code === -14) {
                notify({
                    type: "error",
                    title: $t({ id: "WithdrawError2FA" }),
                });
                formErrorEvent('-14');
                return;
            }
            if (data.code === -15) {
                notify({
                    type: "error",
                    title: $t({ id: "WithdrawErrorNoDeposit" }),
                });
                formErrorEvent('-15');
                return;
            }
            if (data.code === -17) {
                notify({
                    type: "error",
                    title: $t({ id: "WithdrawErrorInProgress" }),
                });
                formErrorEvent('-17');
                return;
            }
            if (data.code === -22) {
                notify({
                    type: "error",
                    title: $t({ id: "WithdrawErrorSingleLimit" }),
                });
                formErrorEvent('-22');
                return;
            }
            if (status === 200 && data.code === 0) {
                notify({
                    type: "success",
                    title: $t({ id: "WithdrawSuccessSubmitted" }),
                });
                form.event({
                    event: 'form_submit',
                    form_id: 'withdraw_cash_form',
                    form_name: 'Withdraw Cash Form',
                });
                dispatch(setLoginType(LoginType.HIDE));
                return;
            }
            notify({
                type: "error",
                title: `Withdraw error ${data.code}`,
            });
        },
    });

    const feeAmount = useMemo(() => {
        const c = payProviderList.find((x) => x.tokenId === category?.id && x.providerName === formik.values.channelCode);
        if (!c) {
            return 0;
        }
        return Math.max(c.minWithdrawGasAmount, c.gasWithdraw * Number(formik.values.amount));
    }, [formik.values.amount, formik.values.channelCode, category, payProviderList]);

    useEffect(() => {
        const newInitialValues = generateInitialValues(formik.values.channelCode + "");

        formik.setValues(newInitialValues);
        formik.setFormikState((prev) => ({
            ...prev,
            validationSchema: formSchema(formik.values.channelCode + ""),
        }));
    }, [category, formik.values.channelCode]);

    useEffect(() => {
        formik.setErrors({});
        formik.setTouched({});
        formik.setFieldValue("channelCode", channelList[0].id);
    }, [category]);

    useEffect(() => {
        const keys = Object.keys(formik.errors);
        keys.forEach((item) => {
            form.error({
                field: item,
                message: formik.errors[item as keyof TFormikConfig] as string,
                form_id: 'withdraw_cash_form',
                form_name: 'Withdraw Cash Form',
            });
        });
    }, [formik.errors]);

    const renderDynamicFields = () => {
        const fields = depositCashConfig[formik.values?.channelCode] || [];
        return fields.map((field) => {
            const fieldName = field.name;

            if (field.ifShow) {
                const { field: showField, value } = field.ifShow;
                if (formik.values[showField] !== value) {
                    return null;
                }
            }

            if (field.type === "select") {
                const selectOptions =
                    field.options?.map((v) => {
                        return {
                            id: v.value,
                            name: v.label,
                        };
                    }) || [];
                return (
                    <Forms.WalletForm.FormRow key={field.name}>
                        <FormSelect
                            size="sm"
                            options={selectOptions}
                            placeholder={$t({ id: field.label })}
                            label={$t({ id: field.label })}
                            name={field.name}
                            value={formik.values[fieldName] + ""}
                            errors={!!formik.errors[fieldName] && !!formik.touched[fieldName] ? { message: $t({ id: "FieldIsRequired" }) } : null}
                            onChange={(val) => {
                                formik.setFieldValue(fieldName, val[0]);
                            }}
                            onFocus={() => formik.setFieldTouched(fieldName, false)}
                            onBlur={() => formik.setFieldTouched(fieldName, true)}
                        />
                    </Forms.WalletForm.FormRow>
                );
            }

            return (
                <Forms.WalletForm.FormRow key={field.name}>
                    <FormInput
                        size="sm"
                        placeholder={$t({ id: field.label })}
                        name={field.name}
                        type={field.type === "phone" ? "number" : field.type}
                        errorMsgAbsolute
                        label={$t({ id: field.label })}
                        value={formik.values[fieldName] || ""}
                        onChange={(e) => {
                            formik.setFieldValue(fieldName, e);
                        }}
                        onBlur={() => formik.setFieldTouched(fieldName, true)}
                        errors={!!formik.errors[fieldName] && !!formik.touched[fieldName] ? { message: $t({ id: "FieldIsRequired" }) } : null}
                    />
                </Forms.WalletForm.FormRow>
            );
        });
    };

    return (
        <Forms.WalletForm.Wrapper title={$t({ id: "WithdrawCash" })}>
            <form
                id="withdraw_cash_form"
                onSubmit={formik.handleSubmit}
            >
                <Forms.WalletForm.FormRow>
                    <FormSelect
                        size="sm"
                        options={channelList}
                        placeholder={$t({ id: "PaymentProvider" })}
                        label={$t({ id: "PaymentProvider" })}
                        name="channelCode"
                        value={formik.values?.channelCode as string}
                        errors={
                            !!formik.errors?.channelCode && !!formik.touched?.channelCode
                                ? {
                                    message: formik.errors?.channelCode,
                                }
                                : null
                        }
                        onChange={(val) => formik.setFieldValue("channelCode", val[0])}
                        onFocus={() => {
                            formik.setFieldTouched("channelCode", false);
                        }}
                        onBlur={() => {
                            formik.setFieldTouched("channelCode", true);
                        }}
                    />
                </Forms.WalletForm.FormRow>
                <Forms.WalletForm.FormRow>
                    <FormInput
                        size="sm"
                        placeholder={$t({ id: "Amount" })}
                        name="amount"
                        type="text"
                        errorMsgAbsolute
                        label="Withdraw amount"
                        value={formik.values?.amount}
                        errors={
                            !!formik.errors?.amount && !!formik.touched?.amount
                                ? {
                                    message: formik.errors?.amount,
                                }
                                : null
                        }
                        onChange={(val) => formik.setFieldValue("amount", val)}
                        onFocus={() => formik.setFieldTouched("amount", false)}
                        onBlur={() => formik.setFieldTouched("amount", true)}
                    />
                </Forms.WalletForm.FormRow>

                {renderDynamicFields()}

                <Forms.WalletForm.BottomGroup>
                    <Forms.WalletForm.FormRow>
                        <Forms.WalletForm.FormText textCenter noMargin boldColor="#0ff">
                            {$t(
                                { id: "YouWillReceiveA" },
                                {
                                    A: (
                                        <span>
                                            {CurrencySymbol[category?.id as keyof typeof CurrencySymbol]}
                                            {formik.values.amount ? Math.max(0, Number(formik.values.amount) - feeAmount).toFixed(5) : 0}
                                        </span>
                                    ),
                                },
                            )}
                        </Forms.WalletForm.FormText>
                    </Forms.WalletForm.FormRow>

                    <Forms.WalletForm.FormRow>
                        <GradientButton
                            id="withdraw_cash_form_submit_button"
                            fullWidth
                            kind="fill"
                            type="submit"
                            text={$t({ id: "ConfirmDeposit" })}
                            disabled={!formik.isValid}
                        />
                    </Forms.WalletForm.FormRow>
                </Forms.WalletForm.BottomGroup>
            </form>
        </Forms.WalletForm.Wrapper>
    )
}

WithdrawCashForm.displayName = 'WithdrawCashForm';